import { UserAccess } from '#server/models';
import { Spinner } from '@/components/Common';
import { trpc } from '@/data-loaders';
import { useHandleError, useHandleSuccess, useUserAccessDetails } from '@/hooks';
import { useAwmsStore } from '@/store';
import { Loader } from 'lucide-react';
import { useEffect, useState } from 'react';

interface UserAccessListingProps {
  userId: string;
}
export const UserAccessListing = ({ userId }: UserAccessListingProps) => {
  const trpcUtils = trpc.useUtils();
  const handleError = useHandleError();
  const sendToastRevokeUserAccessSuccess = useHandleSuccess({ title: 'Revoke user access' });
  const sendToastRevokeAllUserAccessSuccess = useHandleSuccess({ title: 'Revoke all user access' });
  const activeYear = useAwmsStore((state) => state.activeYear);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const {
    isLoading: isRevokingUserAccessLoading,
    isError: revokingUserAccessError,
    mutate: revokeUserAccessMutation,
  } = trpc.admin.revokeUserAccess.useMutation({
    onSuccess: async () => {
      sendToastRevokeUserAccessSuccess();
      handleInvalidateAndRefetch();
    },
    onError: (error) => {
      handleError([error]);
    },
  });

  const {
    isLoading: isRevokingAllUserAccessLoading,
    isError: revokingAllUserAccessError,
    mutate: revokeAllUserAccessMutation,
  } = trpc.admin.revokeAllUserAccess.useMutation({
    onSuccess: async () => {
      sendToastRevokeAllUserAccessSuccess();
      handleInvalidateAndRefetch();
    },
    onError: (error) => {
      handleError([error]);
    },
  });

  const {
    data: userAccessDetails,
    isLoading: isUserAccessDetailsLoading,
    isFetching: isUserAccessDetailsFetching,
    error: userAccessDetailsError,
  } = useUserAccessDetails(userId);

  const handleRevokeAccess = (userAccess: UserAccess) => {
    revokeUserAccessMutation({
      userId: userAccess.userId,
      schoolId: userAccess.schoolId,
      accessLevel: userAccess.accessLevel,
      year: activeYear,
    });
  };
  const handleRevokeAllAccess = (userAccess: UserAccess) => {
    revokeAllUserAccessMutation({
      userId: userAccess.userId,
      year: activeYear,
    });
  };

  const handleInvalidateAndRefetch = async () => {
    trpcUtils.admin.getUserAccessStats.invalidate();
    trpcUtils.admin.getUserAccessStats.refetch();
    trpcUtils.admin.getUserAccessDetails.invalidate();
    trpcUtils.admin.getUserAccessDetails.refetch();
  };

  useEffect(() => {
    setIsPageLoading(isRevokingUserAccessLoading || isRevokingAllUserAccessLoading || isUserAccessDetailsLoading);
  }, [isPageLoading, isRevokingUserAccessLoading, isUserAccessDetailsLoading, isRevokingAllUserAccessLoading]);
  // useEffect(() => { //TODO: explore why we need to remove this
  //   handleError([revokingUserAccessError, revokingAllUserAccessError, userAccessDetailsError]);
  // }, [handleError, revokingAllUserAccessError, revokingUserAccessError, userAccessDetailsError]);

  if (isPageLoading) return <Loader />;

  const sortedUserAccessDetails = userAccessDetails?.sort((a, b) => a.schoolName.localeCompare(b.schoolName));
  return (
    <div>
      {sortedUserAccessDetails && sortedUserAccessDetails.length > 0 && !isUserAccessDetailsFetching && (
        <div className="ml-8 mt-10 flex flex-col gap-2">
          <table className="min-w-full border-collapse border border-gray-300">
            <thead>
              <tr>
                <th className="border border-gray-300 px-4 py-2">School</th>
                <th className="border border-gray-300 px-4 py-2">Access Level</th>
                <th className="border border-gray-300 px-4 py-2">Action</th>
                {sortedUserAccessDetails.length > 1 && <th className="border border-gray-300 px-4 py-2"></th>}
              </tr>
            </thead>
            <tbody>
              {sortedUserAccessDetails?.map((userAccess, index) => {
                return (
                  <tr key={userAccess.curtinId + userAccess.accessLevel + userAccess.schoolName} className="border-t">
                    <td className="border-2 px-4 py-2">{userAccess.schoolName}</td>
                    <td className="border-2 px-4 py-2">{userAccess.accessLevel}</td>
                    <td
                      className="cursor-pointer border-2 px-4 py-2 text-b2 font-semibold text-deep-blue underline"
                      onClick={() => handleRevokeAccess(userAccess)}
                    >
                      Revoke access
                    </td>
                    {sortedUserAccessDetails.length > 1 && index === 0 && (
                      <td
                        className="cursor-pointer border-2 px-4 py-2 text-b2 font-semibold text-deep-blue underline"
                        rowSpan={sortedUserAccessDetails.length}
                        onClick={() => handleRevokeAllAccess(userAccess)}
                      >
                        Revoke all access
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}{' '}
      {isUserAccessDetailsFetching && <Spinner />}
    </div>
  );
};
